import { OneMind } from "./1mind";
import { ChiliPiper } from "./chili-piper";
import { HubSpot } from "./hubspot";
import { HubspotForms } from "./hubspot-forms";
import { OneTrust } from "./onetrust";

/**
 * ThryvApi — the single namespace for our page-level vendor APIs, published as `window.ThryvApi`.
 *
 * One global, one place to document, one boot call in app.js. Everything here is a plain API, not
 * an Alpine component, and every member loads its vendor script only on first use — carrying the
 * namespace costs a few KB, using it is what fetches anything.
 *
 *   ThryvApi.oneMind.embed({ container: "#host" });      1Mind AI advisor  (thryv-api/1mind)
 *   ThryvApi.chiliPiper.book({ lead, router });          demo booking      (thryv-api/chili-piper)
 *   ThryvApi.oneTrust.optedIn();                         cookie consent    (thryv-api/onetrust)
 *   ThryvApi.hubspot.create({ target, form });           HubSpot forms     (thryv-api/hubspot)
 *   ThryvApi.hubspotForms.on(name, { ready, … });        form block scripts (thryv-api/hubspot-forms)
 *
 * ONE NAME PER MEMBER, camelCase, matching its folder. There were `cp` and `"1mind"` aliases for
 * the names people say out loud; they were removed. Four ways to write the same call is four things
 * to search for when something breaks, and `"1mind"` could not even be written as a plain property —
 * an identifier cannot start with a digit, which is the same reason the module exports `OneMind`.
 *
 * ── WHY A NAMESPACE ──────────────────────────────────────────────────────────────────────────
 * The vendor launcher defines `window.onemind` and `window.oneMindConfig`. Ours was
 * `window.OneMind` — case-sensitively distinct, so it worked, but two globals differing only in
 * capitalisation is a trap for anyone debugging, and it collides outright if the vendor ever
 * changes casing. Namespacing removes the question.
 */
export const ThryvApi = {
  version: "1.2.0",

  oneMind: OneMind,
  chiliPiper: ChiliPiper,
  oneTrust: OneTrust,
  hubspot: HubSpot,
  hubspotForms: HubspotForms,

  /**
   * Boot every member, then publish the namespace.
   *
   * Called once per page by whichever bundle is in play: app.js on native pages, and
   * legacy/src2/ThryvApi/index.js — which imports this same module — on legacy ones. A page gets one
   * or the other, never both, because native assets are gated behind RenderMode::isNativeRequest().
   */
  boot() {
    OneMind.boot();
    ChiliPiper.boot();
    OneTrust.boot();
    HubSpot.boot();
    HubspotForms.boot();

    return this.expose();
  },

  expose() {
    window.ThryvApi = this;

    return this;
  },
};

export default ThryvApi;
